import { AsyncJobStatus, IPublication } from '@opengov/stories-shared/types';
import {
  IModalMessages,
  IPublicationReport,
  IPublicationReportEmbedCookie,
  IPublicationReportListing,
  IPublicationViewMessages,
  IReportEmbedTypes,
  ISessionUser,
  ISettings,
  ISortOptions,
  IStoryEmbedsResponse,
  IStoryListMessages,
  IStoryListSelection,
  IStoryShowOptions
} from '../../../../../types';
import React, { Dispatch, useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { usePublicationReportEmbeds } from '../hooks/usePublicationReportEmbeds';
import { useDeleteReportEmbeds, useUpdateDataFrequency } from '../hooks/usePublicationReportEmbedMutations';
import { Map } from '@opengov/compose-app/immutable';
import { BulkUpdateReportDataModal } from '../BulkUpdateReportDataModal';
import { BulkDeleteReportEmbedModal } from '../BulkDeleteReportEmbedModal';
import { ControlBarReport } from '../../ControlBarReport';
import { ReportSelectionToolbar } from '../ReportSelectionToolbar';
import { EmbedPopulationProcessLoader } from '../ReportEmbedsPopulatingLoader/ReportEmbedsPopulatingLoader';
import { Reports } from '../../../../constants/publications';
import {
  REPORT_EMBED_CREATED_BY,
  REPORT_EMBED_SORT,
  REPORT_EMBED_TYPE,
  SHOW_ALL,
  SORT_UPDATED_NEW_TO_OLD,
  STATE_ALL_REPORT_EMBEDS
} from '@opengov/stories-shared/constants';
import Cookies from 'universal-cookie';
import { HasuraSubscription, MessageListener, getNewReportEmbedCookieValue } from '../../../../utils';
import { Box, Button, Typography } from '@mui/material';
import { styles } from './PublicationReportEmbedContent.styles';
import { ICurrentOrLegacyReport } from '@opengov/reporting-types';
import {
  getLocalStorageParsedValue,
  removeOrUpdateLocalStorageValue,
  setLocalStorageParsedValue
} from '../../../../utils/localStorageUtils';
import { populateReportEmbedSubscriptionQuery } from '../../../../graphQL/queries';
import { EmptyViewContent } from '../../../CommonComponents/EmptyViewContent';
import { PublicationReportsTable } from '../PublicationReportsTable/PublicationReportsTable';
import { AddTileContainer } from '../../../../../modals/components/AddTile/AddTileContainer';
import { AddTableTileContainer } from '../../../../../modals/components/AddTableTile/AddTableTileContainer';
import { ADD_TABLE_TILE, ADD_TILE } from '../../../../constants';
import { useStory, useStoryContents } from '../../../StoryView/hooks';

type IPublicationReportEmbedContent = {
  entityId: number;
  entitySubdomain: string;
  entityUuid: string;
  messages: IPublicationViewMessages;
  modalMessages: IModalMessages;
  storyMessages: IStoryListMessages;
  publication: IPublication;
  user: ISessionUser;
  isAdmin: boolean;
  settings: ISettings;
  isReportEmbedListSelected: boolean;
  reports: ICurrentOrLegacyReport[];
  selectedSectionId: string;
  showReports: boolean;
  rowsPerPageReportEmbed: number;
  setSelectedSection: (section: IStoryListSelection, viewtype?: string) => Promise<void>;
  setRowsPerPageReportEmbed: (value: number) => void;
  setIsCreatePublicationModalVisible: Dispatch<React.SetStateAction<boolean>>;
  setView: (value: React.SetStateAction<string>) => void;
  isNewReportTileModalEnabled: boolean;
};
const cookies = new Cookies();

export const PublicationReportEmbedContent = (props: IPublicationReportEmbedContent) => {
  const {
    entityId,
    entitySubdomain,
    entityUuid,
    messages,
    modalMessages,
    storyMessages,
    publication,
    user,
    isAdmin,
    settings,
    isReportEmbedListSelected,
    reports,
    selectedSectionId,
    showReports,
    rowsPerPageReportEmbed,
    setSelectedSection,
    setRowsPerPageReportEmbed,
    setIsCreatePublicationModalVisible,
    setView,
    isNewReportTileModalEnabled
  } = props;
  const useNewAddTileUI = isNewReportTileModalEnabled;

  const [tileModal, setTileModal] = useState<{ modal: string; selectedEmbed: IStoryEmbedsResponse } | null>(null);

  const tileStoryId = tileModal?.selectedEmbed.story._id ?? null;
  const tileStoryQuery = useStory(
    tileStoryId ? { storyId: tileStoryId, isTemplate: false } : null,
    ''
  );
  const tileStoryContentsQuery = useStoryContents(
    tileStoryId && entityId > 0 ? { storyId: tileStoryId, entityId, isTemplate: false } : null,
    ''
  );
  const tileStory = tileStoryQuery.data ?? null;
  const tileStoryContents = tileStoryContentsQuery.data ?? [];
  const tileStoryContent = tileModal
    ? tileStoryContents.find((sc) => sc.content?.includes(`data-embed-id="${tileModal.selectedEmbed.embedId}"`)) ??
    tileStoryContents[0] ??
    null
    : null;

  const onOpenTileModal = useCallback((modal: string, selectedEmbed: IStoryEmbedsResponse) => {
    setTileModal({ modal, selectedEmbed });
  }, []);

  const [showBulkReportUpdate, setShowBulkReportUpdate] = useState<boolean>(false);
  const [showBulkReportDelete, setShowBulkReportDelete] = useState<boolean>(false);
  const [currentPageReportEmbed, setCurrentPageReportEmbed] = useState(1);
  const [reportEmbedSearch, setReportEmbedSearch] = useState('');
  const [matchedReportsIds, setMatchedReportsIds] = useState<string[] | null>(null);
  const [filterReportSelected, setFilterReportSelected] = useState<boolean>(false);
  const [selectedReportItems, setSelectedReportItems] = useState<Map<string, IPublicationReport>>(Map([]));
  const [populateEmbedProcessStatus, setPopulateEmbedProcessStatus] = useState<string | null>(null);
  const publicationReportEmbedsCookie = cookies.get('publicationsReportEmbed') as IPublicationReportEmbedCookie;
  const [allReports, setAllReports] = useState<ICurrentOrLegacyReport[]>([]);
  const [failedEmbedUpdateIds, setFailedEmbedUpdateIds] = useState<string[]>([]);
  const allReportsRef = React.useRef(allReports);
  const _setAllReports = (data) => {
    allReportsRef.current = data;
    setAllReports(data);
  };
  const [reportEmbedCreatedBy, setReportEmbedCreatedBy] = useState<IStoryShowOptions>(
    publicationReportEmbedsCookie?.reportEmbedCreatedBy || SHOW_ALL
  );
  const [reportEmbedSort, setReportEmbedSort] = useState<ISortOptions>(
    publicationReportEmbedsCookie?.reportEmbedSort || SORT_UPDATED_NEW_TO_OLD
  );
  const [reportEmbedType, setReportEmbedType] = useState<IReportEmbedTypes>(
    publicationReportEmbedsCookie?.reportEmbedType || STATE_ALL_REPORT_EMBEDS
  );

  // Report-embeds → usePublicationReportEmbeds (§9: entityId > 0
  // guard). Params include all filter/sort/page state so TanStack Query auto-refetches
  // whenever they change, replacing the imperative getPublicationReportEmbeds effect.
  const reportEmbedsParams = useMemo(
    () =>
      publication?.id && entityId > 0
        ? {
          publicationId: publication.id,
          limit: rowsPerPageReportEmbed,
          offset: (currentPageReportEmbed - 1) * rowsPerPageReportEmbed,
          entityId,
          entitySubdomain,
          entityUuid,
          sort: reportEmbedSort,
          type: reportEmbedType,
          authorId: reportEmbedCreatedBy,
          folderIds: matchedReportsIds ?? undefined,
          userId: user.id
        }
        : null,
    [
      publication?.id,
      entityId,
      entitySubdomain,
      entityUuid,
      rowsPerPageReportEmbed,
      currentPageReportEmbed,
      reportEmbedSort,
      reportEmbedType,
      reportEmbedCreatedBy,
      matchedReportsIds,
      user.id
    ]
  );
  const reportEmbedsQuery = usePublicationReportEmbeds(
    reportEmbedsParams,
    { reportOrViewDeleted: messages.reportOrViewDeleted },
    messages.somethingWentWrongError
  );
  const updateDataFrequencyMutation = useUpdateDataFrequency(messages);
  const deleteReportEmbedsMutation = useDeleteReportEmbeds(messages);

  // The tile modal's save flow (AddTile/AddTableTile) writes the embed via GraphQL
  // directly and doesn't know about this query, so the list must be refetched on
  // close to pick up report/view/frequency/title changes without a page reload.

  const onCloseTileModal = useCallback(() => {
    setTileModal(null);
    void (async () => {
      await reportEmbedsQuery.refetch();
      await new Promise((resolve) => setTimeout(resolve, 1500));
      await reportEmbedsQuery.refetch();
    })();
  }, [reportEmbedsQuery]);

  // Derive legacy prop shapes from discriminated query result.
  const publicationReportEmbeds: IPublicationReportListing | null =
    reportEmbedsQuery.data?.kind === 'reports'
      ? { data: reportEmbedsQuery.data.data as unknown as IPublicationReport[], count: reportEmbedsQuery.data.count }
      : reportEmbedsQuery.data?.kind === 'empty'
        ? { data: [], count: 0 }
        : null;
  const populateStoryEmbedJobId =
    reportEmbedsQuery.data?.kind === 'populating'
      ? reportEmbedsQuery.data.populateStoryEmbedsJobId
      : undefined;
  const isFetchingPublicationReports = reportEmbedsParams !== null && reportEmbedsQuery.isLoading;

  const populateEmbedSubscription = new HasuraSubscription({
    query: populateReportEmbedSubscriptionQuery
  });
  const asyncPending =
    populateEmbedProcessStatus === AsyncJobStatus.InProgress || populateEmbedProcessStatus === AsyncJobStatus.Enqueued;

  const showEmptyReportContent =
    !isFetchingPublicationReports && publicationReportEmbeds && !publicationReportEmbeds.count && !asyncPending;

  // Mutations call hooks directly; query invalidation in each
  // mutation's onSettled handles the re-fetch (no explicit getPublicationReportEmbeds needed).
  const handleUpdateDataFrequency = async (embedIds, frequency): Promise<string[]> => {
    const failedEmbeds = await updateDataFrequencyMutation.mutateAsync({
      updatePayload: embedIds,
      frequency,
      publicationId: publication.id,
      userId: user.id
    });
    setSelectedReportItems(Map([]));
    return failedEmbeds ?? [];
  };

  const handleDeleteReport = async (payload: { storyId: string; embedId: string }[]) => {
    await deleteReportEmbedsMutation.mutateAsync({
      deletePayload: payload,
      publicationId: publication.id,
      userId: user.id
    });
    setSelectedReportItems(Map([]));
  };

  const handleRowOnSelectAll = () => {
    if (!publicationReportEmbeds) return;

    let updatedReportItems = Map<string, IPublicationReport>([]);
    const selectedItemArray = Array.from(selectedReportItems.keys());
    const items = publicationReportEmbeds.data.filter(
      (record) => !record.isViewDeleted && !failedEmbedUpdateIds.includes(record.embedId)
    );
    const allSelected =
      items.length && items.every((item) => selectedItemArray.includes(`${item.embedId}-${item.story._id}`));
    if (allSelected) {
      setSelectedReportItems(updatedReportItems);
      return;
    }
    if (selectedReportItems.size !== publicationReportEmbeds.data.length) {
      publicationReportEmbeds.data.forEach((item) => {
        if (!item.isViewDeleted && !failedEmbedUpdateIds.includes(item.embedId)) {
          updatedReportItems = updatedReportItems.set(`${item.embedId}-${item.story._id}`, item);
        }
      });
    }
    setSelectedReportItems(updatedReportItems);
  };

  const handleRowOnSelect = (currentItem: IPublicationReport) => {
    let updatedReportItems = Map<string, IPublicationReport>([]);
    const reportMapId = `${currentItem.embedId}-${currentItem.story._id}`;
    if (!selectedReportItems.size || !selectedReportItems.has(reportMapId))
      updatedReportItems = selectedReportItems.set(reportMapId, currentItem);
    else updatedReportItems = selectedReportItems.remove(reportMapId);
    setSelectedReportItems(updatedReportItems);
  };

  const handleOnRowsPerPageChange = (value: number): void => {
    setSelectedReportItems(Map([]));
    setRowsPerPageReportEmbed(value);
  };

  const handleBulkUpdateReportClose = (): void => {
    setShowBulkReportUpdate(false);
  };

  const handleBulkReportDeleteClose = (): void => {
    setShowBulkReportDelete(false);
  };

  const handleBulkUpdateReportOpen = (): void => {
    setShowBulkReportUpdate(true);
  };

  const handleBulkDeleteReportOpen = (): void => {
    setShowBulkReportDelete(true);
  };

  const setPageReportEmbed = (value: number) => {
    setSelectedReportItems(Map([]));
    setCurrentPageReportEmbed(value);
  };

  const handleReportEmbedSearch = (text) => {
    if (!text) {
      setMatchedReportsIds(null);
      setReportEmbedSearch(text);
    } else {
      const currentAndLegacyReports = allReportsRef?.current;
      const matchedReports = currentAndLegacyReports.filter(
        (report) => report.name.toLowerCase().indexOf(text.toLowerCase()) !== -1
      );
      setMatchedReportsIds(matchedReports.map((report) => report.id));
      setReportEmbedSearch(text);
    }
    setFilterReportSelected(true);
  };

  const updateReportEmbedSort = (sortVal: ISortOptions) => {
    setReportEmbedSort(sortVal);
    cookies.set(
      'publicationsReportEmbed',
      getNewReportEmbedCookieValue(cookies.get('publicationsReportEmbed'), REPORT_EMBED_SORT, sortVal)
    );
    setFilterReportSelected(true);
  };

  const updateReportEmbedType = (type: IReportEmbedTypes) => {
    setReportEmbedType(type);
    cookies.set(
      'publicationsReportEmbed',
      getNewReportEmbedCookieValue(cookies.get('publicationsReportEmbed'), REPORT_EMBED_TYPE, type)
    );
    setFilterReportSelected(true);
  };

  const updateReportEmbedCreatedBy = (userVal: IStoryShowOptions) => {
    setReportEmbedCreatedBy(userVal);
    cookies.set(
      'publicationsReportEmbed',
      getNewReportEmbedCookieValue(cookies.get('publicationsReportEmbed'), REPORT_EMBED_CREATED_BY, userVal)
    );
    setFilterReportSelected(true);
  };

  const clearSearchReportFilters = () => {
    updateReportEmbedSort(SORT_UPDATED_NEW_TO_OLD);
    updateReportEmbedType(STATE_ALL_REPORT_EMBEDS);
    handleReportEmbedSearch('');
    updateReportEmbedCreatedBy(SHOW_ALL);
    cookies.set(
      'publicationsReportEmbed',
      getNewReportEmbedCookieValue(cookies.get('publicationsReportEmbed'), REPORT_EMBED_SORT, SORT_UPDATED_NEW_TO_OLD)
    );
    cookies.set(
      'publicationsReportEmbed',
      getNewReportEmbedCookieValue(cookies.get('publicationsReportEmbed'), REPORT_EMBED_TYPE, STATE_ALL_REPORT_EMBEDS)
    );
    cookies.set(
      'publicationsReportEmbed',
      getNewReportEmbedCookieValue(cookies.get('publicationsReportEmbed'), REPORT_EMBED_CREATED_BY, SHOW_ALL)
    );
    setFilterReportSelected(false);
  };

  const renderClearFilterReport = () => {
    return (
      <Box role="status" aria-live="polite" sx={styles.noStoriesFoundContainer}>
        <Typography variant="h2" data-test="report-none-found-heading">{messages.noReportsFound}</Typography>
        <Typography variant="h3">{!!selectedSectionId ? storyMessages.adjustFiltersInFolder : storyMessages.adjustFilters}</Typography>
        <Box sx={styles.filterResultButtons}>
          <Button variant="outlined" size="large" onClick={clearSearchReportFilters} data-test="clear-filters-button">
            {storyMessages.clearFilters}
          </Button>
          {!!selectedSectionId && (
            <Box sx={styles.allStoryButton}>
              <Button
                variant="contained"
                size="large"
                onClick={() => {
                  clearSearchReportFilters();
                  void setSelectedSection(null as unknown as IStoryListSelection);
                }}
                data-test="view-all-stories-button"
              >
                {messages.viewAllReports}
              </Button>
            </Box>
          )}
        </Box>
      </Box>
    );
  };

  const subscribePopulateEmbedProcessMessages = () => {
    const onMessageReceived: MessageListener = (messageData) => {
      const populateEmbedProcess: string = messageData?.data?.asyncJobs[0]?.jobStatus;
      if (
        !populateEmbedProcess ||
        populateEmbedProcess === AsyncJobStatus.Success ||
        populateEmbedProcess === AsyncJobStatus.Error
      ) {
        populateEmbedSubscription.unsubscribe();
      }
      setPopulateEmbedProcessStatus(populateEmbedProcess);
    };
    populateEmbedSubscription.addMessageListener(onMessageReceived);
    if (populateStoryEmbedJobId) {
      populateEmbedSubscription.subscribe({ variables: { populateEmbedJobId: populateStoryEmbedJobId } });
    }
  };

  const selectedReportListData = selectedReportItems
    ? Array.from(selectedReportItems.values()).map((item) => ({ embedId: item.embedId, storyId: item.story._id }))
    : [];

  useEffect(() => {
    _setAllReports(reports);
  }, [publication.id, reports]);

  useEffect(() => {
    setSelectedReportItems(Map<string, IPublicationReport>([]));
  }, [selectedSectionId]);

  useEffect(() => {
    clearSearchReportFilters();
  }, [publication.id]);

  // On populate-job success, refetch via the hook query (query
  // invalidation could also work, but refetch on the same key is direct/explicit).
  useEffect(() => {
    populateEmbedSubscription.clearAllMessageListeners();
    subscribePopulateEmbedProcessMessages();
    const jobIdInProgress: string = getLocalStorageParsedValue(publication.id)?.populateEmbedProcessId as string;
    if (!jobIdInProgress && !!populateStoryEmbedJobId && !!publication?.id)
      setLocalStorageParsedValue(publication.id, { populateEmbedProcessId: populateStoryEmbedJobId });
    if (populateEmbedProcessStatus === AsyncJobStatus.Success && !!publication.id) {
      removeOrUpdateLocalStorageValue(publication.id, 'populateEmbedProcessId');
      void reportEmbedsQuery.refetch();
    }
    if (populateEmbedProcessStatus === AsyncJobStatus.Error)
      removeOrUpdateLocalStorageValue(publication.id, 'populateEmbedProcessId');
    return () => {
      populateEmbedSubscription.unsubscribe();
    };
  }, [populateEmbedProcessStatus, populateStoryEmbedJobId]);

  // The imperative getPublicationReportEmbeds effect (which re-ran
  // on every filter/page/sort change) is removed. The declarative reportEmbedsParams
  // useMemo encodes those dependencies in the query key — TanStack Query re-fires
  // automatically when any param changes.

  useEffect(() => {
    if (publicationReportEmbedsCookie) {
      if (publicationReportEmbedsCookie.reportEmbedSort) {
        setReportEmbedSort(publicationReportEmbedsCookie.reportEmbedSort);
      }
      if (publicationReportEmbedsCookie.reportEmbedCreatedBy) {
        setReportEmbedCreatedBy(publicationReportEmbedsCookie.reportEmbedCreatedBy);
      }
      if (publicationReportEmbedsCookie.reportEmbedType) {
        setView(publicationReportEmbedsCookie.reportEmbedType);
      }
    }
  }, []);

  return (
    <Box sx={showReports ? {} : styles.hide}>
      <BulkUpdateReportDataModal
        messages={messages}
        selectedReportIds={selectedReportListData}
        updateDataFrequency={handleUpdateDataFrequency}
        onClose={handleBulkUpdateReportClose}
        show={showBulkReportUpdate}
        setFailedEmbedUpdateIds={setFailedEmbedUpdateIds}
      />
      <BulkDeleteReportEmbedModal
        messages={messages}
        selectedReportIds={selectedReportListData}
        deleteReports={handleDeleteReport}
        onClose={handleBulkReportDeleteClose}
        show={showBulkReportDelete}
      />
      {selectedReportItems.size === 0 && (
        <ControlBarReport
          show={reportEmbedCreatedBy}
          sort={reportEmbedSort}
          search={reportEmbedSearch}
          messages={storyMessages}
          type={reportEmbedType}
          updateSearch={handleReportEmbedSearch}
          updateCreatedBy={updateReportEmbedCreatedBy}
          updateReportType={updateReportEmbedType}
          updateSort={updateReportEmbedSort}
        />
      )}
      {selectedReportItems.size > 0 && (
        <ReportSelectionToolbar
          messages={messages}
          selectedItems={selectedReportItems}
          setSelectedItems={setSelectedReportItems}
          openBulkUpdateModal={handleBulkUpdateReportOpen}
          openBulkReportDeleteModal={handleBulkDeleteReportOpen}
        />
      )}
      {asyncPending ? (
        <EmbedPopulationProcessLoader messages={messages} />
      ) : (
        <PublicationReportsTable
          messages={messages}
          data={publicationReportEmbeds ? publicationReportEmbeds.data : []}
          selectedItems={selectedReportItems}
          setSelectedItems={setSelectedReportItems}
          handleRowOnSelect={handleRowOnSelect}
          handleRowOnSelectAll={handleRowOnSelectAll}
          onOpenTileModal={onOpenTileModal}
          failedEmbedUpdateIds={failedEmbedUpdateIds}
          paginationOptions={{
            totalRows: publicationReportEmbeds?.count ?? 0,
            rowsPerPage: rowsPerPageReportEmbed,
            setRowsPerPage: handleOnRowsPerPageChange,
            currentPage: currentPageReportEmbed,
            setPage: setPageReportEmbed,
            itemName: Reports
          }}
        />
      )}
      {showEmptyReportContent && filterReportSelected && renderClearFilterReport()}
      {showEmptyReportContent && !filterReportSelected && (
        <EmptyViewContent
          messages={messages}
          subHeading={messages.addReportEmbedsInPublication}
          heading={messages.welcomeToPublicationReports}
          showButton={false}
          setIsCreatePublicationModalVisible={setIsCreatePublicationModalVisible}
        />
      )}
      {tileModal && tileStory && tileModal.modal === ADD_TILE &&
        createPortal(
          <AddTileContainer
            isTemplate={false}
            entityId={entityId}
            entitySubdomain={entitySubdomain}
            story={tileStory}
            storyContents={tileStoryContents}
            settings={settings}
            messages={modalMessages}
            storyContent={(tileStoryContent ?? { content: '' }) as any}
            selectedEmbed={tileModal.selectedEmbed}
            isReportEmbedListSelected={isReportEmbedListSelected}
            useNewAddTileUI={useNewAddTileUI}
            isAdmin={isAdmin}
            publicationId={publication.id}
            onClose={onCloseTileModal}
          />,
          document.getElementById('app') as Element
        )}
      {tileModal && tileStory && tileModal.modal === ADD_TABLE_TILE &&
        createPortal(
          <AddTableTileContainer
            isTemplate={false}
            entityId={entityId}
            entitySubdomain={entitySubdomain}
            story={tileStory}
            storyContents={tileStoryContents}
            messages={modalMessages}
            storyContent={(tileStoryContent ?? { content: '' }) as any}
            selectedEmbed={tileModal.selectedEmbed}
            isReportEmbedListSelected={isReportEmbedListSelected}
            publicationId={publication.id}
            onClose={onCloseTileModal}
          />,
          document.getElementById('app') as Element
        )}
    </Box>
  );
};
